/**
 * useDealerData Hook
 * Handles fetching and managing dealer/territory data
 */

import { useEffect, useCallback } from "react";
import { getUniqueListBy } from "@components/Common/Function";
import { myDealerService } from "../services/myDealerService";
import { formatDate } from "../utils/gpsUtils";
import {
  LOADING_STATES,
  SPECIAL_USER_TYPES,
  MTP_ENABLED_USER_TYPES,
} from "./constants";

export const useDealerData = ({
  selectedTerritory,
  sh3CodeForTerritory,
  tourPlanDefaultData,
  clusterCodeOption,
  territoryOptions,
  userType,
  isChecked,
  setDealerOption,
  setIsDealerData,
  showError,
  setTerritoryPickList,
  setCluster,
  setSH3CodeForTerritory,
  setSelectedTerritory,
  setLoading,
}) => {
  /**
   * Fetch dealers by territory
   */
  const fetchDealers = useCallback(
    async (territoryCode) => {
      if (!territoryCode) return;

      try {
        setIsDealerData(LOADING_STATES.LOADING);
        setLoading(true);

        // Construct payload in hook, pass to service
        const payload = {
          code: territoryCode,
          isjointlyadopteddealer: isChecked,
        };

        const response = await myDealerService.getDealers(payload);

        if (response.success) {
          if (response.data.length === 0) {
            setIsDealerData(LOADING_STATES.NO_DATA);
          } else {
            setDealerOption(response.data);
            setIsDealerData(LOADING_STATES.SUCCESS);
          }
        } else {
          showError(response.message);
          setIsDealerData(LOADING_STATES.NO_DATA);
        }
      } catch (error) {
        console.error("Error fetching dealers:", error);
        showError("Failed to fetch dealer data");
        setIsDealerData(LOADING_STATES.NO_DATA);
      } finally {
        setLoading(false);
      }
    },
    [isChecked, setDealerOption, setIsDealerData, showError, setLoading]
  );

  /**
   * Fetch territories for ZSM users
   */
  const fetchZSMTerritories = useCallback(
    async (sh3Code) => {
      if (!sh3Code) return;

      try {
        const response = await myDealerService.getZSMTerritories(sh3Code);
        if (response.success) {
          setTerritoryPickList(response.data);
        } else {
          setTerritoryPickList([]);
        }
      } catch (error) {
        console.error("Error fetching ZSM territories:", error);
        setTerritoryPickList([]);
      }
    },
    [setTerritoryPickList]
  );

  /**
   * Auto-populate cluster and territory from MTP (Monthly Tour Plan)
   * Mirrors WSS setDefaultValues logic exactly.
   */
  const setDefaultValues = useCallback(() => {
    if (!tourPlanDefaultData || tourPlanDefaultData.length === 0) return;

    const clusterFilterData = getUniqueListBy(
      clusterCodeOption,
      SPECIAL_USER_TYPES.includes(userType)
        ? "CLUSTER_CODE__c"
        : "SH3_Name__c"
    );

    const mtpCurrentDateData = tourPlanDefaultData.filter(
      (item) =>
        formatDate(item.MTP_Date__c) === formatDate(new Date()) 
      // &&
      //   item.Objectives_es__c &&
      //   item.Objectives_es__c.includes("Dealers Visit")
    );

    // Helper: resolve territory code from MTP data row
    // Tries relationship field first (MTP_Territory_es__r), falls back to
    // Territory_Cluster_es__c text formula (format "Name:Code:...:Id")
    const getTerritoryCode = (mtpData) => {
      if (mtpData.MTP_Territory_es__r?.SH2_Code__c) {
        return mtpData.MTP_Territory_es__r.SH2_Code__c;
      }
      if (mtpData.Territory_Cluster_es__c) {
        return mtpData.Territory_Cluster_es__c.split(":")[1] || null;
      }
      return null;
    };

    if (!MTP_ENABLED_USER_TYPES.includes(userType) && userType !== "SH3") {
      // HO Auditor, HO Collaborator, NSM and others:
      // auto-populate territory only (no cluster)
      if (mtpCurrentDateData.length > 0) {
        const mtpData = mtpCurrentDateData[0];
        const territoryCode = getTerritoryCode(mtpData);
        if (territoryCode) {
          const defaultTerritory = (territoryOptions || []).filter(
            (item) => item.SH2_Code__c === territoryCode
          );
          if (defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      }
      return;
    }

    if (mtpCurrentDateData.length > 0) {
      const mtpData = mtpCurrentDateData[0];

      if (MTP_ENABLED_USER_TYPES.includes(userType)) {
        // Set cluster from MTP (for RBDM, MH3, etc.)
        if (mtpData.Cluster_es__c) {
          const parts = mtpData.Cluster_es__c.split(":");
          let defaultCluster = clusterFilterData.filter(
            (item) => item.CLUSTER_CODE__c === parts[1]
          );
          if (!defaultCluster || defaultCluster.length === 0) {
            defaultCluster = clusterFilterData.filter(
              (item) => item.SH3_Code__c === parts[1]
            );
          }
          if (defaultCluster && defaultCluster.length > 0) {
            setCluster(defaultCluster[0]);
            setSH3CodeForTerritory(parts[1]);
          }
        }

        // Set territory from MTP
        const territoryCode = getTerritoryCode(mtpData);
        if (territoryCode) {
          const defaultTerritory = (territoryOptions || []).filter(
            (item) => item.SH2_Code__c === territoryCode
          );
          if (defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      } else {
        // SH3 and others — territory only
        const territoryCode = getTerritoryCode(mtpData);
        if (territoryCode) {
          const defaultTerritory = (territoryOptions || []).filter(
            (item) => item.SH2_Code__c === territoryCode
          );
          if (defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      }
    }
  }, [
    tourPlanDefaultData,
    clusterCodeOption,
    territoryOptions,
    userType,
    setCluster,
    setSH3CodeForTerritory,
    setSelectedTerritory,
  ]);

  /**
   * Effect: Fetch dealers when territory changes  
   */
  useEffect(() => {
    if (selectedTerritory?.SH2_Code__c) {
      fetchDealers(selectedTerritory.SH2_Code__c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritory?.SH2_Code__c, isChecked]);

  /**
   * Effect: Fetch ZSM territories when sh3Code changes
   */
  useEffect(() => {
    if (sh3CodeForTerritory) {
      fetchZSMTerritories(sh3CodeForTerritory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sh3CodeForTerritory]);

  /**
   * Effect: Set default values on mount
   */
  useEffect(() => {
    setDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourPlanDefaultData, clusterCodeOption, territoryOptions, userType]);

  return {
    fetchDealers,
    fetchZSMTerritories,
    setDefaultValues,
  };
};

export default useDealerData;
